import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { projectsAPI, scenariosAPI } from './api';

const ProjectContext = createContext(null);

const STORAGE_KEYS = {
  projectId: 'dip-active-project-id',
  scenarioId: 'dip-active-scenario-id',
};

export function ProjectProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeProject, setActiveProjectState] = useState(null);
  const [activeScenario, setActiveScenarioState] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  // Load projects on mount
  const refreshProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await projectsAPI.list();
      const list = res.data || [];
      setProjects(list);
      // Don't auto-select any project on mount.
      // User must explicitly navigate to a project to activate it.
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => { refreshProjects(); }, [refreshProjects]);

  // Load scenarios when active project changes
  const refreshScenarios = useCallback(async (projectId) => {
    if (!projectId) { setScenarios([]); return; }
    setLoadingScenarios(true);
    try {
      const res = await scenariosAPI.list(projectId);
      const list = res.data || [];
      setScenarios(list);

      // Restore active scenario from localStorage
      const savedScenarioId = localStorage.getItem(STORAGE_KEYS.scenarioId);
      if (savedScenarioId) {
        const found = list.find(s => s.id === savedScenarioId);
        if (found) setActiveScenarioState(found);
        else {
          localStorage.removeItem(STORAGE_KEYS.scenarioId);
          // Auto-select if only one scenario
          if (list.length === 1) {
            setActiveScenarioState(list[0]);
            localStorage.setItem(STORAGE_KEYS.scenarioId, list[0].id);
          } else {
            setActiveScenarioState(null);
          }
        }
      } else if (list.length === 1) {
        // Auto-select single scenario
        setActiveScenarioState(list[0]);
        localStorage.setItem(STORAGE_KEYS.scenarioId, list[0].id);
      }
    } catch {
      setScenarios([]);
    } finally {
      setLoadingScenarios(false);
    }
  }, []);

  useEffect(() => {
    if (activeProject?.id) refreshScenarios(activeProject.id);
    else { setScenarios([]); setActiveScenarioState(null); }
  }, [activeProject?.id, refreshScenarios]);

  const setActiveProject = useCallback((project) => {
    setActiveProjectState(prev => {
      const changed = prev?.id !== project?.id;
      if (changed) {
        // Clear scenario only when switching to a DIFFERENT project
        setActiveScenarioState(null);
        localStorage.removeItem(STORAGE_KEYS.scenarioId);
      }
      return project;
    });
    if (project?.id) localStorage.setItem(STORAGE_KEYS.projectId, project.id);
    else localStorage.removeItem(STORAGE_KEYS.projectId);
  }, []);

  const setActiveScenario = useCallback((scenario) => {
    setActiveScenarioState(scenario);
    if (scenario?.id) localStorage.setItem(STORAGE_KEYS.scenarioId, scenario.id);
    else localStorage.removeItem(STORAGE_KEYS.scenarioId);
  }, []);

  return (
    <ProjectContext.Provider value={{
      projects, scenarios,
      activeProject, activeScenario,
      setActiveProject, setActiveScenario,
      refreshProjects, refreshScenarios: () => refreshScenarios(activeProject?.id),
      loadingProjects, loadingScenarios,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within ProjectProvider');
  return context;
}
